import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Breadcrumbs, P, H6 } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

//TYPE AND SERVICES

// COMPANY MASTER
import {
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

// INDUSTRIES
import {
  industriesService,
  INDUSTRY_QUERY_KEY,
} from "../../../../../Services/industriesService";
import { Industries } from "../../../../../Types/MasterData/Industries";
import {
  countryService,
  COUNTRY_QUERY_KEY,
} from "../../../../../Services/countryService";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  company_code: string;
  company_name: string;
}

const UpdateCompanyNameForm: React.FC<UpdateFormProps> = ({
  toggle,
  company_code,
  company_name,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    company_code: company_code,
    company_name: "",
    start_date: "",
    end_date: "",
    owner_name: "",
    industry: 0,
    tin_id: "",
    remarks: "",
  });

  //FETCH COMPANY DETAILS
  const { data: CompanyData, isLoading: isFetching } = useQuery({
    queryKey: [COMPANY_QUERY_KEY, company_code],
    queryFn: () => companyprofileService.getCompanyProfile(company_code),
  });

  useEffect(() => {
    if (CompanyData) {
      setFormData({
        company_code: CompanyData.data.company_code || company_code,
        company_name: CompanyData.data.company_name || "",
        start_date: CompanyData.data.start_date || "",
        end_date: CompanyData.data.end_date || "",
        owner_name: CompanyData.data.owner_name || "",
        industry: CompanyData.data.industry || "",
        tin_id: CompanyData.data.tin_id || "",
        remarks: CompanyData.data.remarks || "",
      });
    }
  }, [CompanyData]);

  //INDUSTRY LIST OF VALUE (ACTIVE)
  const { data: industryData, isLoading: isIndustryLoading } = useQuery<{
    data: Industries[];
  }>({
    queryKey: INDUSTRY_QUERY_KEY,
    queryFn: industriesService.getAllIndustries,
  });

  const industries: Industries[] =
    industryData?.data?.filter((item) => item.status === 1) ?? [];

  const updateCompanyMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      companyprofileService.updateCompanyProfile(company_code, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      toast.success(result.message);
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update client profile.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateCompanyMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <H6 className="fw-bold">Company Information</H6>
          <Col md="3">
            <Label>
              Company Code<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-1"
              id="company_code"
              name="company_code"
              type="text"
              value={formData.company_code}
              disabled={true}
            />
          </Col>
          <Col md="9">
            <Label>
              Client Name<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-1"
              id="name"
              name="company_name"
              type="text"
              placeholder="Loading..."
              maxLength={150}
              minLength={3}
              aria-required
              value={formData.company_name}
              onChange={handleInputChange}
              disabled={updateCompanyMutation.isPending}
            />
          </Col>

          <Col md="3">
            <Label>TIN</Label>
            <Cleave
              className="form-control mb-1"
              options={{
                delimiter: "-",
                blocks: [3, 3, 3, 3],
                uppercase: true,
              }}
              id="tin_id"
              name="tin_id"
              type="text"
              placeholder="Enter Company TIN"
              maxLength={150}
              minLength={5}
              value={formData.tin_id}
              onChange={handleInputChange}
              disabled={updateCompanyMutation.isPending}
              required
            />
          </Col>

          <Col md="4">
            <Label>
              Industry<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-1"
              value={formData.industry}
              id="industry"
              name="industry"
              type="select"
              onChange={handleInputChange}
              required
              disabled={updateCompanyMutation.isPending}
            >
              <option value="" disabled>
                {isIndustryLoading ? "Loading..." : "Select Industry"}
              </option>
              {!isIndustryLoading &&
                industries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="5">
            <Label>Chief Executive Officer</Label>
            <Input
              className="form-control mb-1"
              id="owner_name"
              name="owner_name"
              type="text"
              placeholder="Enter Founder or Contact Person"
              maxLength={150}
              minLength={5}
              value={formData.owner_name}
              onChange={handleInputChange}
              disabled={updateCompanyMutation.isPending}
            />
          </Col>

          <H6 className="fw-bold">Partnership Details</H6>

          <Col md="3">
            <Label>
              Start Date<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-1"
              id="start_date"
              name="start_date"
              type="date"
              required
              value={formData.start_date}
              onChange={handleInputChange}
              disabled={updateCompanyMutation.isPending}
            />
          </Col>

          <Col md="3">
            <Label>End Date</Label>
            <Input
              className="form-control mb-1"
              id="end_date"
              name="end_date"
              type="date"
              value={formData.end_date}
              onChange={handleInputChange}
              min={formData.start_date}
              disabled={updateCompanyMutation.isPending}
            />
          </Col>

          <Col md="6">
            <Label>
              Remarks
              {formData.end_date ? <b className="text-danger">*</b> : ""}
            </Label>
            <Input
              className="form-control mb-1"
              id="remarks"
              name="remarks"
              type="text"
              required={!!formData.end_date}
              placeholder="Enter End Date Reason"
              value={formData.remarks}
              onChange={handleInputChange}
              disabled={updateCompanyMutation.isPending}
            />
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateCompanyMutation.isPending}
            >
              {updateCompanyMutation.isPending
                ? "Updating Client Profile..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateCompanyNameForm;
